import type { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Body1, Stack } from '@myphx-engineering/phx-design-system';
import {
    isInsuranceOfferType,
    isSelfPayOfferType,
} from '@phx/data-sources/medication-marketplace';

import { useMedicationConfigOverride } from '../../../../contexts/DrugProgramProvider';
import { useFillPrescription } from '../../../../contexts/fill/use-fill-prescription';
import { getFragment } from '../../../../graphql/client/helpers/get-fragment';
import { useDrugName } from '../../../../hooks/use-drug-name';
import { useProgramTerms } from '../../../../hooks/use-program-terms';
import { processProviderToPharmacyInfo } from '../../../../util';

export const SelfPayCoverageDescription: FC = () => {
    const { t } = useTranslation('lilly');
    const configOverride = useMedicationConfigOverride();

    const { medication, offers } = useFillPrescription();
    const drugName = useDrugName(medication);

    // If the copay assistance card specifically requires commercial insurance, then patients with Government insurance (e.g. Medicaid) _must_ use self-pay
    const insuranceTerms = useProgramTerms(medication, 'insurance');
    const govtSelfPayOnly =
        insuranceTerms?.savingsCard?.insuranceRequirement === 'ANY_COMMERCIAL';

    const selfPayOffers = offers.filter((offer) =>
        isSelfPayOfferType(offer.headlinePriceType)
    );
    const insuranceOffers = offers.filter((offer) =>
        isInsuranceOfferType(offer.headlinePriceType)
    );

    const pharmacy = getFragment(selfPayOffers[0]?.provider);
    const pharmacyInfo = pharmacy
        ? processProviderToPharmacyInfo(pharmacy, configOverride)
        : null;
    const pharmacyName = pharmacyInfo?.name;

    const line1 =
        pharmacyName && selfPayOffers.length === 1 ? (
            <Trans
                t={t}
                i18nKey="fill.selfPay.pathSelect.line1SingleOffer"
                components={{ 1: <strong /> }}
                values={{ pharmacyName }}
            />
        ) : govtSelfPayOnly ? (
            <Trans
                t={t}
                i18nKey="fill.selfPay.pathSelect.line1Commercial"
                components={{ 1: <strong /> }}
            />
        ) : (
            <Trans
                t={t}
                i18nKey="fill.selfPay.pathSelect.line1"
                components={{ 1: <strong /> }}
            />
        );
    const line2 =
        drugName && insuranceOffers.length === 0 ? (
            govtSelfPayOnly ? (
                <Trans
                    t={t}
                    i18nKey="fill.selfPay.pathSelect.line2NoPricingCommercial"
                    components={{ 1: <strong /> }}
                    values={{ drugName }}
                />
            ) : (
                <Trans
                    t={t}
                    i18nKey="fill.selfPay.pathSelect.line2NoPricing"
                    components={{ 1: <strong /> }}
                    values={{ drugName }}
                />
            )
        ) : (
            t('fill.selfPay.pathSelect.line2')
        );
    return (
        <Stack gap="sm" data-testid="path-select-description">
            <Body1>{line1}</Body1>
            <Body1>{line2}</Body1>
        </Stack>
    );
};
